import Layout from 'domain/entity/layout';
import React, { createContext, ReactNode, useContext } from 'react';
import { isSome, none, Option } from 'fp-ts/lib/Option';
import ReadLayoutsCriteria from 'use-case/layout/read/readLayoutsCriteria';
import { useReportTemplateDependencies } from 'adapter/in/reports/reportsDependencies';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { useUseCaseAlerts } from 'adapter/in/common/hooks/useUseCaseAlerts';

export type ReportLayoutsDataState = {
    layouts: Option<Layout[]>;
    hasLayoutFetchError: boolean;
    isLoadingLayouts: boolean;
};

export const initialState: ReportLayoutsDataState = {
    layouts: none,
    hasLayoutFetchError: false,
    isLoadingLayouts: false,
};

type ReportsViewStateDispatch = {
    getLayouts: (criteria: ReadLayoutsCriteria) => void;
    resetLayouts: () => void;
};

export type ReportsViewStateProviderProps = {
    children: ReactNode;
};

export const ReportLayoutDataStateDispatchContext = createContext({} as ReportsViewStateDispatch);
const ReportLayoutsDataStateContext = createContext({} as ReportLayoutsDataState);

const dispatchReportLayoutsData = (): ReportsViewStateDispatch => useContext(ReportLayoutDataStateDispatchContext);
const useReportLayoutData = (): ReportLayoutsDataState => useContext(ReportLayoutsDataStateContext);

const ReportLayoutDataProvider = (props: ReportsViewStateProviderProps) => {
    const { children } = props;
    const { getLayouts: getLayoutsClient } = useReportTemplateDependencies();
    const { data, error, execute, reset, isLoading } = useCase(getLayoutsClient, false);

    useUseCaseAlerts(data, error, { errorMessage: 'reports.reportLayouts.loadError' });

    const getLayouts = (criteria: ReadLayoutsCriteria) => {
        execute(criteria);
    };
    const resetLayouts = () => {
        reset();
    };

    return (
        <ReportLayoutsDataStateContext.Provider value={{ layouts: data, hasLayoutFetchError: isSome(error), isLoadingLayouts: isLoading }}>
            <ReportLayoutDataStateDispatchContext.Provider value={{ getLayouts, resetLayouts }}>
                {children}
            </ReportLayoutDataStateDispatchContext.Provider>
        </ReportLayoutsDataStateContext.Provider>
    );
};

export { ReportLayoutDataProvider, dispatchReportLayoutsData, useReportLayoutData };
